import DayJs from 'utils/Dayjs';
import { nanoid } from 'nanoid';
import { concat, times, groupBy, flatMap } from 'lodash';

// Helper function for segmenting
const parts = (arr, n) => {
	const chunks = [];
	while (arr.length) {
		const size = Math.ceil(arr.length / n--);
		const chunky = arr.slice(0, size);
		chunks.push(chunky);
		arr = arr.slice(size);
	}
	return chunks;
};

// Generate study plan
export const generateStudyPlan = (start, end, courses) => {
	// Do not generate anything if dates ar not selected
	if (!start || !end) return courses;

	// Create dayjs instances for fixed plan generation
	// aka 01.09.same-year - 31.05.next-year
	const fixedStart = DayJs(start).month(8).date(1).startOf('day');
	const fixedEnd = DayJs(start).add(1, 'year').month(4).date(31).endOf('day');

	// Create dayjs instances from selected dates
	const startDate = DayJs(start).startOf('day');
	const endDate = DayJs(end).endOf('day');

	// Check if fixed plan, aka 01.09.same-year - 31.05.next-year
	const isFixed = startDate.isSame(fixedStart, 'day') && endDate.isSame(fixedEnd, 'day');

	// Generate either fixed or fluid study plan
	return isFixed 
		? generateFixedStudyPlan(courses, startDate)
		: generateFluidStudyPlan(courses, startDate, endDate);
};

// Generate fixed study plan
const generateFixedStudyPlan = (courses, startDate) => {
	// Partition courses by order
	const partitions = flatMap(groupBy(courses, 'order'), (group) => [group]);

	// Append start and end dates to each course
	return concat(...partitions.map((part, sIdx) => {
		// Calculate part's startDate
		const partStartDate = startDate.add(sIdx, 'month').startOf('day');
		const partEndDate = partStartDate.endOf('month').endOf('day');
	
		// Return courses and exams with their dates
		return part.map(course => ({
			...course,
			startDate: partStartDate.toISOString(),
			endDate: partEndDate.toISOString(),
			exam: course.exam ? {
				...course.exam,
				startDate: partStartDate.toISOString(),
				endDate: partEndDate.toISOString()
			} : undefined
		}));
	}));
};

// Generate fluid study plan
const generateFluidStudyPlan = (courses, startDate, endDate) => {
	// Cache how many days and months are between selected dates
	const days = endDate.diff(startDate, 'day');
	let months = endDate.diff(startDate, 'month');

	// Do not allow longer segments than 30 days
	if (days / months > 30) months += 1;

	// Calculate segment's course duration
	// How many days are in each segment
	const segmentDurationMod = days % months;
	const segmentDuration = segmentDurationMod === 0
		? days / months
		: (days - segmentDurationMod) / months + 1;

	// Chunk courses into segments
	const segments = parts(courses, months);

	// Append start and end dates to each course
	return concat(...segments.map((segment, sIdx) => {
		// Calculate segment's startDate
		const segmentStartDate = sIdx === 0
			? startDate
			: startDate.add(segmentDuration * sIdx, 'days').startOf('day');

		// Calculate segment's endDate
		const segmentEndDate = sIdx === segments.length - 1
			? endDate
			: segmentStartDate.add(segmentDuration - 1, 'day').endOf('day');

		// Return courses and exams with their dates
		return segment.map(course => ({
			...course,
			startDate: segmentStartDate.toISOString(),
			endDate: segmentEndDate.toISOString(),
			exam: course.exam ? {
				...course.exam,
				startDate: segmentStartDate.toISOString(),
				endDate: segmentEndDate.toISOString()
			} : undefined
		}));
	}));
};

// Generates study plan for given grade
// with startDate and endDate
export const studyPlanGenerator = (grade) => {
	// Do not generate anything if dates ar not selected
	if (!grade.startDate || !grade.endDate) return grade.courses;

	// Create dayjs instances for fixed plan generation
	// aka 01.09.same-year - 31.05.next-year
	const fixedStart = DayJs(grade.startDate).month(8).date(1).startOf('day');
	const fixedEnd = DayJs(grade.startDate).add(1, 'year').month(4).date(31).endOf('day');

	// Create dayjs instances from selected dates
	const startDate = DayJs(grade.startDate).startOf('day');
	const endDate = DayJs(grade.endDate).endOf('day');

	// Cache how many days and months are between selected dates
	const days = endDate.diff(startDate, 'day');
	let months = endDate.diff(startDate, 'month');

	// Check if fixed plan, aka 01.09 - 31.05
	const isFixed = startDate.isSame(fixedStart, 'day') && endDate.isSame(fixedEnd, 'day');

	// Do not allow longer segments than 30 days
	if (days / months > 30) months += 1;

	// Set fixed month amount on fixed generation
	if (isFixed) months = 9;

	// Calculate segment's course duration
	// How many days are in each segment
	const segmentDurationMod = days % months;
	const segmentDuration = segmentDurationMod === 0
		? days / months
		: (days - segmentDurationMod) / months + 1;

	// Chunk courses into segments
	const segments = parts(grade.courses, months);

	// Append start and end dates to each course
	return concat(...segments.map((segment, sIdx) => {
		// Calculate segment's startDate
		const segmentStartDate = sIdx === 0
			? startDate
			: startDate.add(segmentDuration * sIdx, 'days').startOf('day');

		// Calculate segment's endDate
		const segmentEndDate = sIdx === segments.length - 1
			? endDate
			: segmentStartDate.add(segmentDuration - 1, 'day').endOf('day');

		// Return courses and exams with their dates
		return segment.map(course => ({
			...course,
			startDate: segmentStartDate.toISOString(),
			endDate: segmentEndDate.toISOString(),
			exam: course.exam ? {
				...course.exam,
				startDate: segmentStartDate.toISOString(),
				endDate: segmentEndDate.toISOString()
			} : undefined
		}));
	}));
};

// Generates payment plan for given grade
// with startDate and endDate
export const generatePaymentPlan = (start, end, details) => {
	// Do not generate anything if dates ar not selected
	if (!start || !end) return [];

	// Create dayjs instances from selected dates
	const startDate = DayJs(start).endOf('day');
	const endDate = DayJs(end).endOf('day');

	// Cache how many days are between selected dates
	const days = endDate.diff(startDate, 'day');

	// Calculate segment's duration
	// How many days are in each segment
	const segmentDurationMod = days % details.sections;
	const segmentDuration = segmentDurationMod === 0
		? days / details.sections
		: (days - segmentDurationMod) / details.sections + 1;

	// Calculate price in each section
	let priceMod = details.price % details.sections;
	const pricePart = (details.price - priceMod) / details.sections;

	// Create payment plan
	return times(details.sections, (idx) => {
		// Calculate current payment's amount
		const amount = pricePart + priceMod;

		// Reset price module
		priceMod = 0;

		// Calculate payment's deadline
		const deadlineAt = idx === 0
			? startDate
			: startDate.add(segmentDuration * idx, 'days').endOf('day');

		return {
			key: nanoid(),
			type: 'scholarship',
			state: 'scheduled',
			deadlineAt: deadlineAt.date(25).toISOString(),
			amount
		};
	});
};

// Generates payment plan for given grade
// with startDate and endDate
export const paymentPlanGenerator = (grade, details) => {
	// Do not generate anything if dates ar not selected
	if (!grade.startDate || !grade.endDate) return [];

	// Create dayjs instances from selected dates
	const startDate = DayJs(grade.startDate).endOf('day');
	const endDate = DayJs(grade.endDate).endOf('day');

	// Cache how many days are between selected dates
	const days = endDate.diff(startDate, 'day');

	// Calculate segment's duration
	// How many days are in each segment
	const segmentDurationMod = days % details.sections;
	const segmentDuration = segmentDurationMod === 0
		? days / details.sections
		: (days - segmentDurationMod) / details.sections + 1;

	// Calculate price in each section
	let priceMod = details.price % details.sections;
	const pricePart = (details.price - priceMod) / details.sections;

	// Create payment plan
	return times(details.sections, (idx) => {
		// Calculate current payment's amount
		const amount = pricePart + priceMod;

		// Reset price module
		priceMod = 0;

		// Calculate payment's deadline
		const deadlineAt = idx === 0
			? startDate
			: startDate.add(segmentDuration * idx, 'days').endOf('day');

		return {
			key: nanoid(),
			gradeId: grade.id,
			type: 'scholarship',
			state: 'scheduled',
			deadlineAt: deadlineAt.date(25).toISOString(),
			amount
		};
	});
};